import * as React from "react";
import { useFormContext, useWatch } from "react-hook-form";

import {
  TriggerSourceType,
  type TriggerRecord,
} from "@/shared/domain/triggers";
import { Card } from "@/shared/foundation/primitives";
import { RobotoButton } from "@/shared/foundation/primitives/RobotoButton";

import { SampleEventDialog } from "../../Trigger/SampleEventDialog";
import { TriggerSentence } from "../../Trigger/TriggerSentence";
import { previewTriggerRecord } from "../buildTriggerRequest";
import { isSourceComplete } from "../formScope";
import { type TriggerFormValues } from "../triggerFormSchema";

import styles from "./TriggerPreview.module.css";

/** The audit fields the sentence never reads, so a draft can stand in for a record. */
const UNSAVED: Omit<
  TriggerRecord,
  "fires_on" | "condition" | "targets" | "name"
> = {
  trigger_id: "",
  org_id: "",
  enabled: true,
  engine: "v2",
  created: "",
  created_by: "",
  modified: "",
  modified_by: "",
};

/**
 * The trigger restated as it is written, at the foot of the form.
 *
 * It sits between the last section and the submit because that is where it is
 * read: the author has just finished writing the rule and is deciding whether
 * to create it, and this is the one place the whole rule is said at once.
 *
 * It reads the draft rather than the built request: a target with no action
 * chosen yet is simply absent from the sentence instead of blocking it.
 *
 * Its header also offers the sample payload, but only while there is one to
 * show: a sample is a subscribed event dereferenced, so a schedule has none,
 * and neither does a subscription nobody has checked a change on yet.
 */
export function TriggerPreview() {
  const { control } = useFormContext<TriggerFormValues>();
  // Mid-swap the source is half one kind and half the other, and the builder
  // would read a cron off a subscription.
  const preview = useWatch({
    control,
    compute: (values: TriggerFormValues) =>
      isSourceComplete(values.firesOn)
        ? { name: values.name, ...previewTriggerRecord(values) }
        : null,
  });
  const firesOn = useWatch<TriggerFormValues, "firesOn">({
    control,
    name: "firesOn",
  });
  const [sampleOpen, setSampleOpen] = React.useState(false);

  const events =
    firesOn.type === TriggerSourceType.Events ? firesOn.events : [];

  return (
    <Card.Root data-testid="trigger-preview">
      <Card.Header>
        <Card.Title>Trigger preview</Card.Title>
        {events.length > 0 && (
          <Card.Actions>
            <RobotoButton
              variant="ghost"
              size="sm"
              onClick={() => setSampleOpen(true)}
              data-testid="trigger-preview-sample"
            >
              View sample payload
            </RobotoButton>
          </Card.Actions>
        )}
      </Card.Header>
      {/* The rows rule and tint themselves edge to edge, so the body holds
          no padding of its own. */}
      <Card.Body className={styles.body}>
        {preview !== null && (
          <TriggerSentence record={{ ...UNSAVED, ...preview }} />
        )}
      </Card.Body>
      {/* Mounted only while open: the dialog fetches the samples on mount, and
          most authors never open it. */}
      {sampleOpen && (
        <SampleEventDialog
          open
          onClose={() => setSampleOpen(false)}
          events={events}
        />
      )}
    </Card.Root>
  );
}
